import { Axios } from '../api';
import { REGISTERS_API_URL } from '../constants';
import { type AppDatabase } from '../db';
import { type Staff } from '../models';
import { toBooleanValueFields } from '../transform';
import { BOOLEAN_VALUE, type MappedStaffResponse } from '../types';

export class StaffService {
    constructor(private readonly db: AppDatabase) {
        this.db = db;
    }

    subscribeToPinChange(callback: (staffId: string) => void) {
        const listener = (_mod: object, primaryKey: string) => {
            const modification = _mod as Partial<Staff>;

            if (!modification.registerPin) {
                return;
            }

            callback(primaryKey);
        };

        this.db.staff.hook('updating', listener);
        return () => this.db.staff.hook('updating').unsubscribe(listener);
    }

    subscribeToDeletion(callback: (staffId: string) => void) {
        const listener = (_mod: object, primaryKey: string) => {
            const modification = _mod as Partial<Staff>;

            if (modification.deleted !== BOOLEAN_VALUE.TRUE) {
                return;
            }

            callback(primaryKey);
        };

        this.db.staff.hook('updating', listener);
        return () => this.db.staff.hook('updating').unsubscribe(listener);
    }

    async fetchAndBulkUpsert() {
        const response = await Axios.get<MappedStaffResponse[]>(
            REGISTERS_API_URL.REGISTER.USER,
        );

        await this.bulkUpsert(response.data);
    }

    async bulkUpsert(staffList: MappedStaffResponse[]) {
        const output = staffList.map((staff) => {
            return toBooleanValueFields(staff, ['deleted', 'superRole']);
        });

        await this.db.staff.bulkPut(output as unknown as Staff[]);
    }

    async getByRegisterPin(registerPin: string) {
        return this.db.staff
            .where('[registerPin+deleted]')
            .equals([registerPin, BOOLEAN_VALUE.FALSE])
            .first();
    }

    async getById(id: string) {
        return this.db.staff
            .where('id')
            .equals(id)
            .and((staff) => !staff.deleted)
            .first();
    }

    async getAll() {
        return this.db.staff
            .where('deleted')
            .equals(BOOLEAN_VALUE.FALSE)
            .toArray();
    }
}
